import { IMediaItem } from 'types/media'

export interface IResolveVisualMediaPlacementParams {
  playheadTime: number
  newDuration: number
  existingMediaItems: IMediaItem[]
}

export interface IResolvedVisualMediaPlacement {
  startTime: number
  zIndex: number
}

/** Half-open intervals [start, start + duration) */
export const intervalsOverlap = (
  aStart: number,
  aDuration: number,
  bStart: number,
  bDuration: number
): boolean => {
  const aEnd = aStart + aDuration
  const bEnd = bStart + bDuration
  return aStart < bEnd && bStart < aEnd
}

const rowHasOverlapAt = (
  rowItems: IMediaItem[],
  playheadTime: number,
  newDuration: number
): boolean =>
  rowItems.some(item =>
    intervalsOverlap(playheadTime, newDuration, item.startTime, item.duration)
  )

/**
 * Free time on a row (zIndex) from `startTime` up to the nearest clip that
 * starts after it. Returns Infinity when nothing lies to the right — callers
 * `Math.min` this against a desired duration to avoid overlapping the next clip.
 */
export const getAvailableDurationOnRow = (
  mediaItems: IMediaItem[],
  zIndex: number,
  startTime: number
): number =>
  mediaItems.reduce(
    (gap, item) =>
      item.zIndex === zIndex && item.startTime > startTime
        ? Math.min(gap, item.startTime - startTime)
        : gap,
    Infinity
  )

export const groupMediaItemsByZIndex = (
  items: IMediaItem[]
): Map<number, IMediaItem[]> => {
  const map = new Map<number, IMediaItem[]>()
  for (const item of items) {
    const list = map.get(item.zIndex) ?? []
    list.push(item)
    map.set(item.zIndex, list)
  }
  return map
}

/**
 * Place new visual media at the playhead on the timeline's top row (highest zIndex).
 * If that row has no time overlap at the playhead, reuse it; if it overlaps,
 * add a new row **above** (front) with zIndex = max(existing zIndex) + 1.
 * Rows are ordered like getDistinctZIndicesDescending: higher z = top line.
 */
export const resolveVisualMediaPlacement = ({
  playheadTime,
  newDuration,
  existingMediaItems
}: IResolveVisualMediaPlacementParams): IResolvedVisualMediaPlacement => {
  if (existingMediaItems.length === 0) {
    return { startTime: playheadTime, zIndex: 0 }
  }

  const byZ = groupMediaItemsByZIndex(existingMediaItems)
  const rowZs = [...byZ.keys()]
  const maxZ = Math.max(...rowZs)
  const topRowItems = byZ.get(maxZ) ?? []

  if (!rowHasOverlapAt(topRowItems, playheadTime, newDuration)) {
    return { startTime: playheadTime, zIndex: maxZ }
  }

  return { startTime: playheadTime, zIndex: maxZ + 1 }
}
